import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import api from "@/lib/api";
import { Reveal, MaskLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import PostCard from "@/components/PostCard";
import { PROFILE, IMG } from "@/data/profile";

const DRONE_REEL = [
  { country: "United Kingdom", src: "https://videos.pexels.com/video-files/15512791/15512791-uhd_2560_1440_30fps.mp4" },
  { country: "Japan", src: "https://videos.pexels.com/video-files/6613438/6613438-uhd_2560_1440_30fps.mp4" },
  { country: "France", src: "https://videos.pexels.com/video-files/36492321/15473967_2560_1440_25fps.mp4" },
  { country: "Singapore", src: "https://videos.pexels.com/video-files/33479088/14242591_2560_1440_30fps.mp4" },
  { country: "United Arab Emirates", src: "https://videos.pexels.com/video-files/31466135/13417444_2560_1440_30fps.mp4" },
  { country: "United States", src: "https://videos.pexels.com/video-files/37493554/15884011_2560_1440_30fps.mp4" },
  { country: "Pakistan", src: "https://videos.pexels.com/video-files/36353872/15419389_1920_1080_30fps.mp4" },
];

function DroneBanner() {
  const [index, setIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const current = DRONE_REEL[index];

  const advance = () => {
    setIndex((i) => (i + 1) % DRONE_REEL.length);
    setReady(false);
  };

  return (
    <div className="relative" data-testid="hero-video-banner">
      <div className="relative aspect-video max-h-[620px] w-full overflow-hidden bg-navy">
        <video
          key={current.src}
          className="h-full w-full object-cover"
          src={current.src}
          poster={ready ? undefined : IMG.hero}
          autoPlay
          muted
          loop={false}
          playsInline
          onEnded={advance}
          onError={advance}
          onLoadedMetadata={(e) => {
            e.currentTarget.play().catch(() => {});
          }}
          onCanPlay={(e) => {
            setReady(true);
            e.currentTarget.play().catch(() => {});
          }}
        />
        <AnimatePresence mode="wait">
          <motion.p
            key={current.country}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6 }}
            className="absolute bottom-5 left-6 bg-navy/70 px-4 py-2 font-serif text-lg italic text-white backdrop-blur-sm sm:left-12 sm:text-2xl lg:left-20"
            data-testid="hero-video-country"
          >
            {current.country}
          </motion.p>
        </AnimatePresence>
      </div>
      <div className="bg-navy">
        <div className="wrap flex items-center justify-between py-3">
          <p className="text-[11px] font-medium tracking-[0.05em] text-white/80">
            Research-led strategy — economic analysis, urban planning & deal advisory
          </p>
          <p className="hidden items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50 sm:flex">
            Aerial views of {DRONE_REEL.length} countries · ~10 min reel
          </p>
        </div>
      </div>
    </div>
  );
}

function Counter({ to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <span ref={ref}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
}

function Hero() {
  return (
    <section className="pt-16 lg:pt-20">
      <DroneBanner />
      <div className="wrap pb-14 pt-14 lg:pb-20 lg:pt-20">
        <h1
          data-testid="hero-headline"
          className="font-serif font-medium leading-[1.06] tracking-[-0.02em] text-5xl text-navy sm:text-7xl lg:text-8xl"
        >
          <MaskLine delay={0.25}>Complex research,</MaskLine>
          <MaskLine delay={0.4}>
            <span className="italic text-blue">translated into</span>
          </MaskLine>
          <MaskLine delay={0.55}>C-suite strategy.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-navy-soft sm:text-lg"
        >
          Economic analysis, market sizing (TDT) and data-driven urban planning — for public- and
          private-sector decisions. This is the portfolio and working journal.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/blog" data-testid="hero-cta-button" className="btn-primary">
            Read the journal
          </Link>
          <Link to="/research" className="btn-ghost">
            Explore research
          </Link>
        </motion.div>
      </div>

      <div className="wrap grid grid-cols-2 gap-6 py-14 md:grid-cols-4 lg:py-16">
        {PROFILE.stats.map((s) => (
          <Reveal key={s.label}>
            <p className="font-serif text-3xl text-navy sm:text-4xl">
              <Counter to={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
            </p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-mute">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Bento() {
  return (
    <section className="bg-paper-band py-24 lg:py-32">
      <div className="wrap">
        <Reveal>
          <p className="overline-mono">The practice</p>
          <h2 className="max-w-2xl font-serif text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Six disciplines, one bias for evidence.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-12">
          <Reveal className="lg:col-span-7" delay={0}>
            <div data-testid="expertise-card-0" className="group relative flex h-full min-h-[380px] flex-col justify-end overflow-hidden border border-line">
              <img src={IMG.research1} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent" />
              <div className="relative p-8 lg:p-10">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/70">01</p>
                <h3 className="mt-3 font-serif text-2xl text-white lg:text-3xl">{PROFILE.expertise[0].title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/80">{PROFILE.expertise[0].blurb}</p>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.1}>
            <div data-testid="expertise-card-1" className="flex h-full min-h-[380px] flex-col justify-between border border-line bg-white p-8 transition-colors duration-300 hover:border-blue lg:p-10">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">02</p>
              <div>
                <h3 className="font-serif text-2xl text-navy lg:text-3xl">{PROFILE.expertise[1].title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-navy-soft">{PROFILE.expertise[1].blurb}</p>
              </div>
              <p className="font-serif text-6xl italic text-line-strong lg:text-7xl">TDT</p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0}>
            <div data-testid="expertise-card-2" className="group relative flex h-full min-h-[340px] flex-col justify-end overflow-hidden border border-line">
              <img src={IMG.research2} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent" />
              <div className="relative p-8 lg:p-10">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/70">03</p>
                <h3 className="mt-3 font-serif text-2xl text-white lg:text-3xl">{PROFILE.expertise[2].title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/80">{PROFILE.expertise[2].blurb}</p>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div data-testid="expertise-card-3" className="flex h-full min-h-[340px] flex-col justify-between border border-line bg-white p-8 transition-colors duration-300 hover:border-blue lg:p-10">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">04</p>
                <h3 className="mt-3 font-serif text-2xl text-navy lg:text-3xl">{PROFILE.expertise[3].title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-navy-soft">{PROFILE.expertise[3].blurb}</p>
              </div>
              <div className="flex flex-wrap gap-2 pt-6">
                {["Readiness scorecards", "GCC strategy", "Adoption roadmaps"].map((chip) => (
                  <span key={chip} className="border border-line px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-soft">
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          {[4, 5].map((i) => (
            <Reveal key={i} className="lg:col-span-6" delay={0.05 * (i - 4)}>
              <div
                data-testid={`expertise-card-${i}`}
                className="flex h-full min-h-[260px] flex-col justify-between border border-line bg-white p-8 transition-colors duration-300 hover:border-blue lg:p-10"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">0{i + 1}</p>
                <div>
                  <h3 className="font-serif text-2xl text-navy">{PROFILE.expertise[i].title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy-soft">{PROFILE.expertise[i].blurb}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestWriting() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);
  const featured = posts[0];
  const rest = posts.slice(1, 4);

  return (
    <section className="py-24 lg:py-32">
      <div className="wrap">
        <Reveal>
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="overline-mono">From the desk</p>
              <h2 className="font-serif text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Recent papers & essays
              </h2>
            </div>
            <div className="flex gap-6 text-[11px] font-semibold uppercase tracking-[0.2em]">
              <Link to="/research" className="text-navy-soft transition-colors hover:text-blue">
                All research →
              </Link>
              <Link to="/blog" className="text-navy-soft transition-colors hover:text-blue">
                All essays →
              </Link>
            </div>
          </div>
        </Reveal>

        {featured && (
          <Reveal>
            <Link
              to={`/writing/${featured.slug}`}
              className="group mb-6 grid border border-line bg-white transition-colors duration-300 hover:border-blue lg:grid-cols-2"
            >
              <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[420px]">
                <img
                  src={featured.cover_url}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex flex-col justify-center gap-5 p-8 lg:p-14">
                <div className="flex flex-wrap items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.2em]">
                  <span className="bg-blue px-2.5 py-1 text-white">Featured</span>
                  <span className="text-blue">{featured.type === "paper" ? "Research Paper" : "Essay"}</span>
                  <span className="text-navy-mute">{featured.category}</span>
                </div>
                <h3 className="font-serif text-3xl leading-snug text-navy transition-colors duration-300 group-hover:text-blue lg:text-4xl">
                  {featured.title}
                </h3>
                <p className="text-sm leading-relaxed text-navy-soft sm:text-base">{featured.excerpt}</p>
                <p className="border-t border-line pt-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-mute">
                  {new Date(featured.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {featured.read_time} min read
                </p>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p, i) => (
            <PostCard key={p.slug} post={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutTeaser() {
  return (
    <section className="bg-paper-band py-24 lg:py-32">
      <div className="wrap grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <div className="relative">
            <div className="overflow-hidden border border-line">
              <img src={IMG.about} alt="" className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 -z-10 h-full w-full border border-blue/40" aria-hidden="true" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="overline-mono">About</p>
          <h2 className="font-serif text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
            A researcher's discipline, a consultant's bias for action.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-navy-soft sm:text-lg">{PROFILE.summary}</p>
          <p className="mt-4 text-base leading-relaxed text-navy-soft">
            The through-line across Buro Happold, ISG and TCS: rigorous research that ends in a decision
            someone can defend — in the boardroom, in the ministry, in the deal room.
          </p>
          <Link to="/about" className="btn-ghost mt-10">
            The full story
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={PROFILE.marquee} />
      <Bento />
      <LatestWriting />
      <AboutTeaser />
    </>
  );
}